import {ReactNode, useCallback, useEffect, useMemo, useState} from 'react';
import {UseMutationResult} from '@tanstack/react-query';
import {useDebounceValue} from 'usehooks-ts';
import ComboboxContent, {ComboboxContentProps, ComboboxValue} from '@/components/form/combobox/combobox-content';

const DEBOUNCE_DELAY = 500;
const MIN_QUERY_LENGTH = 2;

interface ComboboxAsyncProps<T extends ComboboxValue>
  extends Omit<ComboboxContentProps<T>, 'values' | 'isLoading' | 'showEmptyResults' | 'setQuery' | 'onChange'> {
  fetchSearchResultsMutation: UseMutationResult<T[], Error, string, unknown>;
  searchResults: T[];
  isLoading?: boolean;
  setSearchResults: (values: T[]) => void;
  onChange: (value: NoInfer<T> | null) => void;
}

const ComboboxAsync = <T extends ComboboxValue>({
  ['data-testid']: dataTestId,
  selectedValue,
  placeholder,
  multiple = false,
  fetchSearchResultsMutation,
  searchResults,
  isLoading = false,
  setSearchResults,
  onChange,
  onFocus,
  onBlur,
  ...props
}: ComboboxAsyncProps<T>): ReactNode | Promise<ReactNode> => {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useDebounceValue(query, DEBOUNCE_DELAY);

  const {mutateAsync, isPending, isSuccess} = fetchSearchResultsMutation;

  const showEmptyResults = useMemo(
    () => debouncedQuery.length >= MIN_QUERY_LENGTH && searchResults.length === 0 && isSuccess && !isPending,
    [debouncedQuery, isSuccess, isPending, searchResults.length],
  );

  const onChangeSelection = useCallback(
    (value: NoInfer<ComboboxValue> | null) => {
      setQuery('');
      setDebouncedQuery('');
      onChange?.(value as T);
    },
    [onChange, setDebouncedQuery],
  );

  const onQueryChange = useCallback(
    async (query: string) => {
      const response = await mutateAsync(query);
      setSearchResults(response);
    },
    [mutateAsync, setSearchResults],
  );

  useEffect(() => {
    if (debouncedQuery.length < MIN_QUERY_LENGTH) {
      setSearchResults([]);
      return;
    }

    onQueryChange(debouncedQuery);
  }, [debouncedQuery, onQueryChange, setSearchResults]);

  return (
    <ComboboxContent<T>
      {...props}
      data-testid={dataTestId}
      placeholder={placeholder}
      multiple={multiple}
      selectedValue={selectedValue}
      values={searchResults}
      isLoading={isLoading || isPending}
      showEmptyResults={showEmptyResults}
      showMenuToggle={false}
      setQuery={setQuery}
      onChange={onChangeSelection}
      onFocus={onFocus}
      onBlur={onBlur}
    />
  );
};

export {type ComboboxAsyncProps, ComboboxAsync as default};
